import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { bboxTone, ocrPalette } from "./ocrPalette";

/**
 * DocumentPreview — fixed-width source-image strip with bounding-box highlight.
 *
 * Props:
 *  pages: [{ src, alt? }]
 *  activePage: number (1-based)
 *  onPageChange: (page) => void
 *  highlight: { bbox: [x, y, w, h], tone: "ok"|"warn"|"fail" } | null
 *    bbox is normalized 0–1 relative to the original image, so it scales
 *    automatically with zoom and any rendered size.
 */
export default function DocumentPreview({ pages = [], activePage, onPageChange, highlight }) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [layoutSize, setLayoutSize] = useState(null);
  const scrollRef = useRef(null);
  const imgRef = useRef(null);

  const page = pages[activePage - 1];
  const total = pages.length;

  const measureImage = useCallback(() => {
    const img = imgRef.current;
    const container = scrollRef.current;
    if (!img?.naturalWidth || !container) return;

    const available = container.clientWidth;
    const fitScale = Math.min(1, available / img.naturalWidth);
    setLayoutSize({
      width: img.naturalWidth * fitScale,
      height: img.naturalHeight * fitScale,
    });
  }, []);

  useEffect(() => {
    setLayoutSize(null);
    setZoom(1);
    setRotation(0);
  }, [page?.src]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => measureImage());
    observer.observe(container);
    return () => observer.disconnect();
  }, [measureImage, page?.src]);

  function changeZoom(delta) {
    setZoom((z) => Math.min(3, Math.max(0.5, +(z + delta).toFixed(2))));
  }

  const tone = highlight ? bboxTone[highlight.tone] || bboxTone.ok : null;
  const zoomedWidth = layoutSize ? layoutSize.width * zoom : undefined;
  const zoomedHeight = layoutSize ? layoutSize.height * zoom : undefined;

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        bgcolor: ocrPalette.surface,
        border: `1px solid ${ocrPalette.border}`,
        borderRadius: "10px",
        p: 1.25,
        alignSelf: "start",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: ocrPalette.text }}>
          Source document
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <PreviewIconButton ariaLabel="Zoom out" onClick={() => changeZoom(-0.25)}>
            <RemoveIcon sx={{ fontSize: 16 }} />
          </PreviewIconButton>
          <PreviewIconButton ariaLabel="Zoom in" onClick={() => changeZoom(0.25)}>
            <AddIcon sx={{ fontSize: 16 }} />
          </PreviewIconButton>
          <PreviewIconButton
            ariaLabel="Rotate"
            onClick={() => setRotation((r) => (r + 90) % 360)}
          >
            <RefreshIcon sx={{ fontSize: 16 }} />
          </PreviewIconButton>
        </Stack>
      </Stack>

      <Box
        ref={scrollRef}
        sx={{
          bgcolor: ocrPalette.grayBg,
          borderRadius: "8px",
          p: 1,
          overflow: "auto",
          maxHeight: 400,
        }}
      >
        {page ? (
          layoutSize ? (
            <Box
              sx={{
                position: "relative",
                width: zoomedWidth,
                height: zoomedHeight,
                flexShrink: 0,
                mx: zoom <= 1 ? "auto" : 0,
                transformOrigin: "top center",
                transition: "width 0.15s ease, height 0.15s ease",
                transform: rotation ? `rotate(${rotation}deg)` : undefined,
              }}
            >
              <Box
                component="img"
                ref={imgRef}
                src={page.src}
                alt={page.alt || `Page ${activePage}`}
                draggable={false}
                sx={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  border: `1px solid ${ocrPalette.imageBorder}`,
                  borderRadius: "4px",
                  bgcolor: "#fff",
                  userSelect: "none",
                }}
              />
              {highlight && rotation === 0 && tone && (
                <Box
                  sx={{
                    position: "absolute",
                    borderRadius: "3px",
                    pointerEvents: "none",
                    transition: "all 0.2s ease",
                    border: `2px solid ${tone.border}`,
                    backgroundColor: tone.bg,
                    left: `${highlight.bbox[0] * 100}%`,
                    top: `${highlight.bbox[1] * 100}%`,
                    width: `${highlight.bbox[2] * 100}%`,
                    height: `${highlight.bbox[3] * 100}%`,
                  }}
                />
              )}
            </Box>
          ) : (
            <Box
              component="img"
              ref={imgRef}
              src={page.src}
              alt={page.alt || `Page ${activePage}`}
              draggable={false}
              onLoad={measureImage}
              sx={{
                display: "block",
                maxWidth: "100%",
                border: `1px solid ${ocrPalette.imageBorder}`,
                borderRadius: "4px",
                bgcolor: "#fff",
                userSelect: "none",
              }}
            />
          )
        ) : (
          <Typography sx={{ fontSize: 11, color: ocrPalette.muted }}>
            No preview available
          </Typography>
        )}
      </Box>

      {total > 1 && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1.25}
          sx={{ fontSize: 12, color: ocrPalette.muted }}
        >
          <PreviewIconButton
            ariaLabel="Previous page"
            disabled={activePage <= 1}
            onClick={() => onPageChange(activePage - 1)}
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </PreviewIconButton>
          <Typography sx={{ fontSize: 12, color: ocrPalette.muted }}>
            Page {activePage} of {total}
          </Typography>
          <PreviewIconButton
            ariaLabel="Next page"
            disabled={activePage >= total}
            onClick={() => onPageChange(activePage + 1)}
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </PreviewIconButton>
        </Stack>
      )}

      {total > 1 && (
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {pages.map((p, i) => {
            const isActive = i + 1 === activePage;
            return (
              <Box
                key={i}
                component="button"
                onClick={() => onPageChange(i + 1)}
                aria-label={`Go to page ${i + 1}`}
                sx={{
                  width: 38,
                  p: 0,
                  border: isActive
                    ? `2px solid ${ocrPalette.accent}`
                    : `1px solid ${ocrPalette.border}`,
                  borderRadius: "4px",
                  bgcolor: "#fff",
                  cursor: "pointer",
                  overflow: "hidden",
                  lineHeight: 0,
                }}
              >
                <Box
                  component="img"
                  src={p.src}
                  alt=""
                  draggable={false}
                  sx={{ display: "block", width: "100%" }}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}

export function PreviewIconButton({ children, ariaLabel, onClick, disabled }) {
  return (
    <IconButton
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      size="small"
      sx={{
        width: 26,
        height: 24,
        borderRadius: "6px",
        border: `1px solid ${ocrPalette.border}`,
        bgcolor: ocrPalette.surface,
        color: ocrPalette.text,
        "&:hover": { bgcolor: ocrPalette.hover },
        "&.Mui-disabled": { opacity: 0.4 },
      }}
    >
      {children}
    </IconButton>
  );
}
